import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, Eye, MousePointerClick, Clock, UserPlus, Activity, Download, RefreshCw, Radio, TrendingUp, TrendingDown } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Spinner } from "../../components/common";

const RANGES = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "custom", label: "Custom" },
];

const fmtDur = (s) => {
  if (!s) return "0s";
  const m = Math.floor(s / 60), sec = Math.round(s % 60);
  return m ? `${m}m ${sec}s` : `${sec}s`;
};

function Card({ icon: Icon, label, value, change }) {
  const up = change != null && change >= 0;
  return (
    <div className="rounded-2xl bg-card border border-border p-4" data-testid={`an-card-${label}`}>
      <div className="flex items-center justify-between">
        <span className="grid place-items-center w-9 h-9 rounded-xl bg-primary/10 text-primary"><Icon size={17} /></span>
        {change != null && (
          <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${up ? "text-success" : "text-destructive"}`}>
            {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{Math.abs(change)}%
          </span>
        )}
      </div>
      <div className="mt-3 text-2xl font-display font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function Bars({ title, rows, nameKey }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-2xl bg-card border border-border p-4">
      <h3 className="font-display font-semibold mb-3">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground py-2">No data yet.</p> : (
        <div className="space-y-2.5">
          {rows.map((r) => (
            <div key={r[nameKey]}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="truncate font-medium">{r[nameKey]}</span>
                <span className="text-muted-foreground shrink-0 ml-2">{r.count} · {r.pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AnalyticsTab() {
  const [range, setRange] = useState("7d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { range };
      if (range === "custom") { params.start = custom.start; params.end = custom.end; }
      const { data } = await api.get("/admin/analytics", { params });
      setData(data);
    } catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, [range, custom]);

  useEffect(() => {
    if (range === "custom" && (!custom.start || !custom.end)) { setLoading(false); return; }
    load();
  }, [load, range, custom]);

  useEffect(() => {
    const id = setInterval(() => { if (range !== "custom") load(); }, 30000);
    return () => clearInterval(id);
  }, [load, range]);

  const doExport = async (format) => {
    try {
      const params = { range, format };
      if (range === "custom") { params.start = custom.start; params.end = custom.end; }
      const res = await api.get("/admin/analytics/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `jackcash-analytics-${range}.${format}`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const c = data?.cards;
  const o = data?.overview;
  const cmp = data?.comparison || {};

  return (
    <div className="space-y-5" data-testid="analytics-tab">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap gap-1.5">
          {RANGES.map((r) => (
            <button key={r.id} data-testid={`an-range-${r.id}`} onClick={() => setRange(r.id)}
              className={`px-3 h-9 rounded-xl text-sm font-semibold transition-colors ${range === r.id ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid="an-refresh" onClick={load} className="grid place-items-center w-9 h-9 rounded-xl bg-secondary text-muted-foreground"><RefreshCw size={15} /></button>
          <button data-testid="an-export-csv" onClick={() => doExport("csv")} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-secondary text-sm font-semibold"><Download size={14} /> CSV</button>
          <button data-testid="an-export-json" onClick={() => doExport("json")} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-secondary text-sm font-semibold"><Download size={14} /> JSON</button>
        </div>
      </div>

      {range === "custom" && (
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="text-xs text-muted-foreground">From</label><input type="date" data-testid="an-custom-start" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="block h-10 px-3 rounded-xl bg-secondary outline-none" /></div>
          <div><label className="text-xs text-muted-foreground">To</label><input type="date" data-testid="an-custom-end" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="block h-10 px-3 rounded-xl bg-secondary outline-none" /></div>
        </div>
      )}

      {loading ? (
        <div className="py-20 grid place-items-center"><Spinner className="w-7 h-7 text-primary" /></div>
      ) : error ? (
        <div className="rounded-2xl bg-destructive/5 border border-destructive/20 p-6 text-center">
          <p className="text-sm text-destructive font-semibold">{error}</p>
          <button onClick={load} className="mt-3 px-4 h-10 rounded-xl bg-primary text-white font-semibold text-sm">Retry</button>
        </div>
      ) : !data ? (
        <div className="rounded-2xl bg-card border border-border p-10 text-center text-muted-foreground">Pick a date range to view analytics.</div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Card icon={Users} label="Visitors" value={c.visitors} change={cmp.visitors_change} />
            <Card icon={Eye} label="Page Views" value={c.page_views} change={cmp.page_views_change} />
            <Card icon={MousePointerClick} label="Sessions" value={c.sessions} />
            <Card icon={Clock} label="Avg Session" value={fmtDur(c.avg_session_duration)} />
            <Card icon={UserPlus} label="Signups" value={c.signups} />
            <Card icon={Activity} label="Active Users" value={c.active_users} />
          </div>

          {/* Traffic chart */}
          <div className="rounded-2xl bg-card border border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">Traffic</h3>
              <div className="flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-primary" /> Visitors</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#22c55e" }} /> Page views</span>
              </div>
            </div>
            {data.timeseries.length === 0 ? (
              <div className="h-64 grid place-items-center text-sm text-muted-foreground">No traffic in this period yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.timeseries} margin={{ left: -18, right: 8, top: 6 }}>
                  <defs>
                    <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6D28D9" stopOpacity={0.35} /><stop offset="100%" stopColor="#6D28D9" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} /><stop offset="100%" stopColor="#22c55e" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.15)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} stroke="rgba(120,120,120,0.5)" />
                  <YAxis tick={{ fontSize: 11 }} stroke="rgba(120,120,120,0.5)" allowDecimals={false} width={38} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(120,120,120,0.2)", fontSize: 12 }} />
                  <Area type="monotone" dataKey="page_views" stroke="#22c55e" fill="url(#gp)" strokeWidth={2} />
                  <Area type="monotone" dataKey="visitors" stroke="#6D28D9" fill="url(#gv)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
            <div className="mt-2 flex gap-6 text-xs text-muted-foreground">
              <span>Unique visitors: <b className="text-foreground">{o.unique_visitors}</b></span>
              <span>New: <b className="text-foreground">{o.new_visitors}</b></span>
              <span>Returning: <b className="text-foreground">{o.returning_visitors}</b></span>
              <span>Avg visit: <b className="text-foreground">{fmtDur(o.avg_visit_duration)}</b></span>
            </div>
          </div>

          {/* Live visitors */}
          <div className="rounded-2xl bg-card border border-border p-4" data-testid="an-live">
            <div className="flex items-center gap-2 mb-3">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-success" /></span>
              <h3 className="font-display font-semibold">Live visitors</h3>
              <span className="text-sm font-bold text-success">{data.live.length} online</span>
            </div>
            {data.live.length === 0 ? <p className="text-sm text-muted-foreground">No one is online right now.</p> : (
              <div className="divide-y divide-border max-h-56 overflow-y-auto">
                {data.live.map((l) => (
                  <div key={l.visitor_id} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-mono truncate">{l.path}</span>
                    <span className="text-muted-foreground shrink-0 ml-3 text-xs">{l.device} · {l.country} · {fmtDur(l.seconds_on_page)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Breakdowns */}
          <div className="grid md:grid-cols-2 gap-4">
            <Bars title="Top pages" rows={data.top_pages} nameKey="path" />
            <Bars title="Traffic sources" rows={data.sources} nameKey="source" />
            <Bars title="Countries" rows={data.countries} nameKey="country" />
            <Bars title="Devices" rows={data.devices} nameKey="device" />
            <Bars title="Browsers" rows={data.browsers} nameKey="browser" />
            <Bars title="Operating systems" rows={data.operating_systems} nameKey="os" />
          </div>

          {/* User activity */}
          <div className="rounded-2xl bg-card border border-border p-4">
            <h3 className="font-display font-semibold mb-3">User activity</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
              {Object.entries(data.user_activity).map(([k, v]) => (
                <div key={k} className="rounded-xl bg-secondary/60 p-3">
                  <div className="text-xl font-display font-semibold">{v}</div>
                  <div className="text-[11px] text-muted-foreground capitalize">{k.replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
